import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ScreenHead, Rise } from '../ui/screen-parts.js';
import { Chip, Modal, Panel, Segments, useFlash } from '../ui/kit.js';
import { Done, Steps, Stepper, money } from '../ui/actions/parts.js';
import { RESIDENT } from '../state/building.js';

import Bruschetta from "../media/images/restaurant-screen/bruschetta.jpg";
import CaesarSalad from "../media/images/restaurant-screen/caesar-salad.jpg";
import StuffedMushrooms from "../media/images/restaurant-screen/stuffed-mushrooms.jpg";
import GarlicBread from "../media/images/restaurant-screen/garlic-bread.jpg";
import GrilledSalmon from "../media/images/restaurant-screen/grilled-salmon.jpg";
import PastaPrimavera from "../media/images/restaurant-screen/pasta-primavera.jpg";
import SteakFrites from "../media/images/restaurant-screen/steak-frites.jpg";
import ChickenParmesan from "../media/images/restaurant-screen/chicken-parmesan.jpg";
import LambChops from "../media/images/restaurant-screen/lamb-chops.jpg";
import Tiramisu from "../media/images/restaurant-screen/tiramisu.jpg";
import Cheesecake from "../media/images/restaurant-screen/cheesecake.jpg";
import ChocolateLavaCake from "../media/images/restaurant-screen/chocolate-lava-cake.png";
import ApplePie from "../media/images/restaurant-screen/apple-pie.jpg";
import Espresso from "../media/images/restaurant-screen/espresso.jpg";
import FreshLemonade from "../media/images/restaurant-screen/fresh-lemonade.jpg";
import IcedTea from "../media/images/restaurant-screen/iced-tea.jpg";
import RedWine from "../media/images/restaurant-screen/red-wine.jpg";

const EASE = [0.16, 1, 0.3, 1];

const RESTAURANT = 'Azul Terrace';
const SERVICE = 0.18;

const menu = {
    appetizers: [
        { name: "Bruschetta", description: "Grilled bread topped with tomato, basil, and olive oil", price: "$8", imageUrl: Bruschetta },
        { name: "Caesar Salad", description: "Romaine lettuce with Caesar dressing, croutons, and Parmesan", price: "$10", imageUrl: CaesarSalad },
        { name: "Stuffed Mushrooms", description: "Baked mushrooms stuffed with garlic and cheese", price: "$9", imageUrl: StuffedMushrooms },
        { name: "Garlic Bread", description: "Warm bread served with garlic butter", price: "$7", imageUrl: GarlicBread }
    ],
    mainCourses: [
        { name: "Grilled Salmon", description: "Served with garlic mashed potatoes and steamed vegetables", price: "$22", imageUrl: GrilledSalmon },
        { name: "Pasta Primavera", description: "Penne pasta with seasonal vegetables and light tomato sauce", price: "$18", imageUrl: PastaPrimavera },
        { name: "Steak Frites", description: "Grilled steak served with French fries and herb butter", price: "$28", imageUrl: SteakFrites },
        { name: "Chicken Parmesan", description: "Breaded chicken breast topped with marinara sauce and mozzarella", price: "$20", imageUrl: ChickenParmesan },
        { name: "Lamb Chops", description: "Grilled lamb chops with mint sauce", price: "$30", imageUrl: LambChops }
    ],
    desserts: [
        { name: "Tiramisu", description: "Classic Italian dessert with mascarpone and espresso", price: "$7", imageUrl: Tiramisu },
        { name: "Cheesecake", description: "Creamy cheesecake with a graham cracker crust", price: "$8", imageUrl: Cheesecake },
        { name: "Chocolate Lava Cake", description: "Warm chocolate cake with a molten center", price: "$9", imageUrl: ChocolateLavaCake },
        { name: "Apple Pie", description: "Homemade apple pie with vanilla ice cream", price: "$7", imageUrl: ApplePie }
    ],
    beverages: [
        { name: "Espresso", description: "Rich Italian coffee", price: "$4", imageUrl: Espresso },
        { name: "Fresh Lemonade", description: "Made with freshly squeezed lemons", price: "$5", imageUrl: FreshLemonade },
        { name: "Iced Tea", description: "Refreshing cold brewed tea", price: "$4", imageUrl: IcedTea },
        { name: "Red Wine", description: "House red wine", price: "$9", imageUrl: RedWine }
    ]
};

const sections = [
    ['Appetizers', menu.appetizers],
    ['Main Courses', menu.mainCourses],
    ['Desserts', menu.desserts],
    ['Beverages', menu.beverages]
];

const ITEMS = Object.fromEntries(sections.flatMap(([, items]) => items).map(item => [
    item.name, { ...item, cost: Number(item.price.replace(/[^0-9.]/g, '')) }
]));

/* How the order reaches the resident, and the steps each way takes.
   `at` is seconds from placing it: a demo watches the whole evening's
   order play out in under twenty seconds. */
const MODES = {
    deliver: {
        label: `Deliver to residence ${RESIDENT.unit}`, short: 'Delivery', minutes: 35,
        times: ['As soon as possible', '7:30 PM', '8:00 PM', '8:30 PM'],
        steps: [{ label: 'Order received', at: 0 }, { label: 'In the kitchen', at: 4 }, { label: 'On its way up', at: 11 }, { label: 'At your door', at: 18 }]
    },
    pickup: {
        label: 'Pick up at the bar', short: 'Pickup', minutes: 20,
        times: ['As soon as possible', '7:30 PM', '8:00 PM', '8:30 PM'],
        steps: [{ label: 'Order received', at: 0 }, { label: 'In the kitchen', at: 4 }, { label: 'Ready at the bar', at: 12 }]
    },
    table: {
        label: `Table at ${RESTAURANT}`, short: 'Table', minutes: 0,
        times: ['7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM'],
        steps: [{ label: 'Order received', at: 0 }, { label: 'Table reserved', at: 4 }, { label: 'Kitchen timed to your arrival', at: 10 }]
    }
};

/* An order outlives the screen, so leaving to check a package and
   coming back finds it still cooking. */
let liveOrder = null;

const clockIn = minutes => new Date(Date.now() + minutes * 60000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function RestaurantScreen() {
    const [flash, notify] = useFlash();
    const [cart, setCart] = useState({});
    const [sheet, setSheet] = useState(null);   // null, 'review' or 'placed'
    const [order, setOrder] = useState(liveOrder);
    const reduced = useReducedMotion();

    const lines = Object.entries(cart).filter(([, qty]) => qty > 0).map(([name, qty]) => ({ ...ITEMS[name], qty }));
    const count = lines.reduce((n, l) => n + l.qty, 0);
    const subtotal = lines.reduce((n, l) => n + l.qty * l.cost, 0);

    const setQty = (name, qty) => setCart(c => ({ ...c, [name]: qty }));

    const add = item => {
        setQty(item.name, (cart[item.name] || 0) + 1);
    };

    const place = details => {
        liveOrder = {
            number: `A-${2040 + Math.floor(Math.random() * 60)}`,
            placedAt: Date.now(),
            lines,
            total: subtotal * (1 + SERVICE),
            ...details
        };
        setOrder(liveOrder);
        setCart({});
        setSheet('placed');
    };

    const clear = () => {
        notify(`Enjoy your evening. Order ${order.number} is complete.`);
        liveOrder = null;
        setOrder(null);
        setSheet(null);
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Restaurant" title="Tonight's menu" />
            {flash}

            {order && sheet !== 'placed' && <OrderStatus order={order} onOpen={() => setSheet('placed')} onFinish={clear} />}

            {sections.map(([heading, items], index) => (
                <Rise className="menu-card" key={heading} index={index}>
                    <h3>{heading}</h3>
                    <ul className="menu-section">
                        {items.map(item => {
                            const qty = cart[item.name] || 0;
                            return (
                                <li key={item.name} className={`menu-item${qty ? ' menu-item--in' : ''}`}>
                                    <img src={item.imageUrl} alt="" className="menu-image" loading="lazy" />
                                    <div className="menu-details">
                                        <h4>{item.name} · {item.price}</h4>
                                        <p>{item.description}</p>
                                    </div>
                                    <div className="menu-item__add">
                                        {qty ? (
                                            <Stepper value={qty} onChange={n => setQty(item.name, n)} max={20} label={item.name} />
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-outline-light btn-sm"
                                                onClick={() => add(item)}
                                                aria-label={`Add ${item.name} to your order`}
                                            >
                                                Add
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </Rise>
            ))}

            {/* The running total rides along the foot of the menu, so the
                order is always one tap away while the reader keeps adding. */}
            <AnimatePresence>
                {count > 0 && sheet === null && (
                    <motion.div
                        className="order-bar"
                        initial={reduced ? { opacity: 0 } : { opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduced ? { opacity: 0 } : { opacity: 0, y: 30 }}
                        transition={{ duration: 0.45, ease: EASE }}
                    >
                        <span className="order-bar__count" aria-hidden="true">{count}</span>
                        <span className="order-bar__text">
                            <strong>Your order</strong>
                            <span>{count} {count === 1 ? 'item' : 'items'} · {money(subtotal)}</span>
                        </span>
                        <button type="button" className="btn btn-primary" onClick={() => setSheet('review')}>
                            View order
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            <OrderSheet
                view={sheet}
                lines={lines}
                subtotal={subtotal}
                order={order}
                onQty={setQty}
                onPlace={place}
                onClose={() => setSheet(null)}
                onFinish={clear}
            />
        </div>
    );
}

/* The order's progress, played on demo time from when it was placed. */
function useProgress(order) {
    const [now, setNow] = useState(() => Date.now());
    const steps = order ? MODES[order.mode].steps : [];
    const last = steps.length ? steps[steps.length - 1].at : 0;
    const elapsed = order ? (now - order.placedAt) / 1000 : 0;
    const finished = Boolean(order) && elapsed >= last;

    useEffect(() => {
        if (!order || finished) return undefined;
        const id = setInterval(() => setNow(Date.now()), 300);
        return () => clearInterval(id);
    }, [order, finished]);

    const step = finished ? steps.length : steps.reduce((at, s, i) => (elapsed >= s.at ? i : at), 0);
    return { steps, step, finished, pct: last ? Math.min(100, (elapsed / last) * 100) : 100 };
}

/* An order in progress, at the top of the menu while the sheet is shut. */
function OrderStatus({ order, onOpen, onFinish }) {
    const { steps, step, finished } = useProgress(order);
    const label = finished ? steps[steps.length - 1].label : steps[step].label;

    return (
        <Panel className="panel--accent order-status">
            <div className="row__end" style={{ justifyContent: 'space-between' }}>
                <div className="row__main">
                    <span className="row__title">Order {order.number} · {MODES[order.mode].short}</span>
                    <span className="row__meta">{order.when}</span>
                </div>
                <div className="row__end">
                    <Chip tone={finished ? 'ok' : 'info'}>{label}</Chip>
                    <button type="button" className="btn btn-outline-light btn-sm" onClick={onOpen}>{finished ? 'View' : 'Track order'}</button>
                    {finished && <button type="button" className="btn btn-primary btn-sm" onClick={onFinish}>Done</button>}
                </div>
            </div>
        </Panel>
    );
}

function OrderSheet({ view, lines, subtotal, order, onQty, onPlace, onClose, onFinish }) {
    const [mode, setMode] = useState('deliver');
    const [time, setTime] = useState(MODES.deliver.times[0]);
    const [guests, setGuests] = useState(2);
    const [notes, setNotes] = useState('');

    const chooseMode = next => {
        setMode(next);
        setTime(MODES[next].times[0]);
    };

    const service = subtotal * SERVICE;
    const total = subtotal + service;

    const place = () => {
        const asap = time === MODES[mode].times[0] && mode !== 'table';
        const when = mode === 'table'
            ? `Table for ${guests} at ${time}, ${RESTAURANT}`
            : asap
                ? `${mode === 'deliver' ? 'Arriving' : 'Ready'} about ${clockIn(MODES[mode].minutes)}`
                : `${mode === 'deliver' ? 'Arriving' : 'Ready'} at ${time}`;
        onPlace({ mode, when, notes: notes.trim(), guests });
        setNotes('');
    };

    const review = view === 'review';
    const empty = lines.length === 0;
    const { finished } = useProgress(review ? null : order);

    const actions = review ? (
        <>
            <button type="button" className="btn btn-outline-light" onClick={onClose}>Keep browsing</button>
            <button type="button" className="btn btn-primary" onClick={place} disabled={empty}>
                Place order · {money(total)}
            </button>
        </>
    ) : finished ? (
        <button type="button" className="btn btn-primary" onClick={onFinish}>Done</button>
    ) : (
        /* Shutting the sheet keeps the order: it carries on at the top
           of the menu until it arrives. */
        <button type="button" className="btn btn-outline-light" onClick={onClose}>Back to the menu</button>
    );

    return (
        <Modal
            open={view !== null}
            onClose={onClose}
            eyebrow={RESTAURANT}
            title={review ? 'Your order' : order ? `Order ${order.number}` : 'Your order'}
            actions={actions}
        >
            {review ? (
                <>
                    {empty ? (
                        <p className="empty">Your order is empty. Add something from the menu.</p>
                    ) : (
                        <ul className="order-lines">
                            {lines.map(line => (
                                <li className="order-line" key={line.name}>
                                    <img className="order-line__thumb" src={line.imageUrl} alt="" />
                                    <span className="order-line__name">
                                        {line.name}
                                        <span className="row__meta">{line.price} each</span>
                                    </span>
                                    <Stepper value={line.qty} onChange={n => onQty(line.name, n)} max={20} label={line.name} />
                                    <span className="order-line__cost">{money(line.qty * line.cost)}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <dl className="order-sum">
                        <div><dt>Subtotal</dt><dd>{money(subtotal)}</dd></div>
                        <div><dt>Service, 18%</dt><dd>{money(service)}</dd></div>
                        <div className="order-sum__total"><dt>Total, to your unit account</dt><dd>{money(total)}</dd></div>
                    </dl>

                    <div className="stack" style={{ gap: '0.5rem' }}>
                        <span className="form-label">How would you like it?</span>
                        <div className="order-modes" role="group" aria-label="How would you like it">
                            {Object.entries(MODES).map(([key, m]) => (
                                <button
                                    type="button"
                                    key={key}
                                    className={`order-mode${mode === key ? ' order-mode--on' : ''}`}
                                    aria-pressed={mode === key}
                                    onClick={() => chooseMode(key)}
                                >
                                    <span className="order-mode__label">{m.label}</span>
                                    <span className="order-mode__note">
                                        {key === 'table' ? 'Your order is ready when you sit down' : `About ${m.minutes} minutes`}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="stack" style={{ gap: '0.5rem' }}>
                        <span className="form-label">{mode === 'table' ? 'Reservation time' : 'When'}</span>
                        <Segments label="When" options={MODES[mode].times} value={time} onChange={setTime} />
                    </div>

                    {mode === 'table' && (
                        <div className="order-guests">
                            <span className="form-label" style={{ margin: 0 }}>Guests</span>
                            <Stepper value={guests} onChange={setGuests} min={1} max={10} label="Guests" />
                        </div>
                    )}

                    <label className="field">
                        <span className="form-label">Allergies or requests</span>
                        <textarea
                            className="form-control"
                            rows="2"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="e.g. No nuts, dressing on the side"
                        />
                    </label>
                </>
            ) : order ? (
                <PlacedOrder order={order} />
            ) : null}
        </Modal>
    );
}

function PlacedOrder({ order }) {
    const { steps, step, finished, pct } = useProgress(order);
    const count = order.lines.reduce((n, l) => n + l.qty, 0);

    return (
        <>
            <Done title={finished ? steps[steps.length - 1].label : 'Order placed'}>
                <p>
                    {order.when}. {count} {count === 1 ? 'item' : 'items'}, {money(order.total)} to your unit account.
                    {order.notes ? ` The kitchen has your note: "${order.notes}".` : ''}
                </p>
            </Done>
            <div className="meter"><span className="meter__fill" style={{ width: `${pct}%`, transition: 'width 0.3s linear' }} /></div>
            <Steps steps={steps} current={step} label="Order progress" />
            <ul className="order-lines order-lines--compact">
                {order.lines.map(line => (
                    <li className="order-line" key={line.name}>
                        <span className="order-line__qty">{line.qty}×</span>
                        <span className="order-line__name">{line.name}</span>
                        <span className="order-line__cost">{money(line.qty * line.cost)}</span>
                    </li>
                ))}
            </ul>
        </>
    );
}

export default RestaurantScreen;
